import { useState } from "react";
import { fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { getDateRange } from "@/lib/utils";
import { toastHandler } from "@/lib/ToastHandler";

// Define the DriversMetrics interface
export interface DriversMetrics {
  active_drivers: number;
  active_worker_types: {
    "full-time": number;
    "part-time": number;
    freelance: number;
  };
  average_road_time_minutes: number;
  drivers_actives_now: number;
  inactive_drivers: number;
  overall_completion_rate_percentage: number;
  overall_delayed_rate_percentage: number;
  total_amount_collected: number;
  total_completed_orders: number;
  total_delayed_orders: number;
  total_drivers: number;
  total_hours_worked: number;
  total_orders: number;
  snapshot_drivers: {
    active_drivers: number;
    inactive_drivers: number;
    active_worker_types: {
      "full-time": number;
      "part-time": number;
      freelance: number;
    };
  };
  trips_per_driver_average: Array<{
    average_trips_per_driver: number;
    date: string;
    total_active_drivers: number;
    total_trips: number;
  }>;
}

// Define filters interface for drivers metrics
export interface DriversMetricsFilters {
  branchId?: string;
  brandId?: string;
  period?: string;
}

export function useDashboardApi() {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAllDriversMetrics = async (
    filters?: DriversMetricsFilters,
  ): Promise<DriversMetrics | null> => {
    const queryParams = new URLSearchParams();

    const { fromDate, toDate } = getDateRange(
      filters?.period as "day" | "week" | "month",
    );

    if (filters?.branchId) queryParams.append("branchId", filters.branchId);
    if (filters?.brandId) queryParams.append("brandId", filters.brandId);
    if (fromDate) queryParams.append("fromDate", fromDate);
    if (toDate) queryParams.append("toDate", toDate);
    queryParams.append("driverId", "");

    const url = `/driver/drivers/metrics/all?${queryParams.toString()}`;

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      return await fetchWithAuth(url, token);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "No se pudieron cargar las métricas de conductores";
      setError(message);
      toastHandler({
        variant: "destructive",
        description: "No se pudieron cargar las métricas. Intente nuevamente.",
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchAllDriversMetrics,
    loading,
    error,
  };
}

export default useDashboardApi;
